// ============================================================================
// File: GovernancePanel.tsx — Spec 160 T018 · reworked by Spec 215 (US5/FR-003/FR-015)
// Author: Peter Dobler | Created: 2026-05-31
// Feature: 215-admin-dashboard-remediation
// Description: The "More coming" placeholder is REPLACED by live collection
//   stats: (1) the compact collection-health status tile ("All N tiers healthy" /
//   "K failing" + last run, linking to the full operational view) — the FR-003
//   home for the signal displaced from the Admin top row; (2) a collection
//   throughput tile (runs 24h, avg duration, runs/day 60d mini-trend).
// Constitution: v1.23.0 | Principle XXXVII, XLV
// ============================================================================
import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Settings, Database, Activity, Gauge } from 'lucide-react';
import { DashboardPanel, Spinner, AppTooltip, ErrorState } from '@/components/ui';
import { Sparkline } from './Sparkline';
import { formatEastern } from '@/utils/dateFormatter';
import type { DashboardKpis, TrendPoint } from '../../../../../shared/types/dashboard';

interface GovernancePanelProps {
  data: DashboardKpis['governance'] | undefined;
  loading?: boolean;
}

const Tile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  trend?: TrendPoint[];
  color?: string;
  tooltip?: React.ReactNode;
}> = ({ icon, label, value, trend, color, tooltip }) => {
  const body = (
    <div className="flex flex-col gap-0.5 rounded-[var(--radius-md)] border border-[var(--color-border)] p-3">
      <div className="flex items-center gap-1.5 text-[var(--color-text-secondary)]">
        {icon}
        <span className="text-xs text-[var(--color-text-secondary)]">{label}</span>
      </div>
      <div className="text-lg font-bold leading-none text-[var(--color-text-primary)]">{value.toLocaleString()}</div>
      {trend && <Sparkline data={trend} color={color} label={label} />}
    </div>
  );
  return tooltip != null ? (
    <AppTooltip title={tooltip} placement="top" className="block"><div>{body}</div></AppTooltip>
  ) : body;
};

const fmtDuration = (sec: number | null): string => {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  return `${Math.round(sec / 60)}m`;
};

export const GovernancePanel: React.FC<GovernancePanelProps> = ({ data, loading = false }) => {
  const t = data?.trends;
  const coll = data?.collection;
  const healthy = (coll?.tiersFailing ?? 0) === 0;
  return (
    <DashboardPanel
      title="Governance Snapshot"
      titleInfo="Governance coverage from the latest collection: workspaces with user/role data, tenant settings captured, datasources discovered, and collection-tier health + throughput. Hover each tile for its definition."
    >
      {loading ? (
        <div className="flex min-h-[120px] items-center justify-center"><Spinner size="md" /></div>
      ) : !data ? (
        // Spec 298 FR-005/FR-006: a section that was not read is said so, never shown as zeros
        <ErrorState title="Could not read the governance figures" />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Tile
            icon={<Users className="h-4 w-4" />}
            label="Workspaces with users"
            value={data.workspaceUsersCovered}
            trend={t?.workspaceUsers}
            color="#6366f1"
            tooltip="Workspaces with at least one collected user/role assignment. The line is the 60-day trend."
          />
          <Tile
            icon={<Settings className="h-4 w-4" />}
            label="Tenant settings"
            value={data.tenantSettingsCount}
            trend={t?.tenantSettings}
            color="#f59e0b"
            tooltip="Fabric tenant-level admin settings captured in the latest collection. The line is the 60-day trend."
          />
          <Tile
            icon={<Database className="h-4 w-4" />}
            label="Datasources"
            value={data.datasourcesCaptured}
            trend={t?.datasources}
            color="#0ea5e9"
            tooltip="Distinct datasources discovered across workspaces (Scanner API). The line is the 60-day trend."
          />
          {/* The compact collection-health tile (replaces the old placeholder cell). */}
          <AppTooltip title="Health of the collection tiers — green when every tier's latest run succeeded. Click for the full run view." placement="top" className="block">
          <Link
            to="/operations/job-monitoring"
            data-testid="collection-health-tile"
            className="flex flex-col gap-0.5 rounded-[var(--radius-md)] border border-[var(--color-border)] p-3 transition-colors hover:border-[var(--color-accent)]"
          >
            <div className="flex items-center gap-1.5 text-[var(--color-text-secondary)]">
              <Activity className="h-4 w-4" />
              <span className="text-xs text-[var(--color-text-secondary)]">Collection health</span>
            </div>
            <div className={`text-lg font-bold leading-none ${healthy ? 'text-[var(--color-status-success)]' : 'text-[var(--color-status-danger)]'}`}>
              {coll == null || coll.tiersTotal === 0
                ? '—'
                : healthy
                  ? `All ${coll.tiersTotal} tiers healthy`
                  : `${coll.tiersFailing} of ${coll.tiersTotal} tiers failing`}
            </div>
            <span className="text-xs text-[var(--color-text-secondary)]">
              {coll?.lastRunAt
                ? `last run ${formatEastern(coll.lastRunAt)}`
                : 'no runs yet'}
            </span>
          </Link>
          </AppTooltip>
          {/* Collection throughput (runs/day mini-trend). */}
          <Tile
            icon={<Gauge className="h-4 w-4" />}
            label={`Collection runs 24h · avg ${fmtDuration(coll?.avgDurationSec ?? null)}`}
            value={data.collection.runs24h}
            trend={t?.collectionRuns}
            color="#10b981"
            tooltip="Collection job runs in the last 24 hours (with average duration). The line is runs-per-day over 60 days."
          />
        </div>
      )}
    </DashboardPanel>
  );
};

export default GovernancePanel;
